// Import a list of stores from a CSV or spreadsheet, or pasted rows.
// Columns are matched by header name; the rep can fix the match. A row
// is skipped when its phone, its website, or its name-plus-city is
// already on the list or earlier in the same file, so one store is never
// added twice and never ends up with two reps. Each batch is tagged with
// an area name so lists can be told apart later.
import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { FileSpreadsheet, RefreshCw, Upload } from 'lucide-react';
import { freshProspectFields, tierTag } from './salesPlan';
import { e164, insertProspects } from './salesData';
import { Field, Sheet, btnGhost, btnPrimary, inputCls } from './salesUi';

const TARGETS = [
  { key: 'name', label: 'Store name', required: true, match: /^(store|name|business|company|store ?name|business ?name|title)$/i },
  { key: 'phone', label: 'Phone', match: /phone|tel|mobile|cell|number/i },
  { key: 'owner_name', label: 'Owner', match: /owner|contact|manager|decision/i },
  { key: 'email', label: 'Email', match: /e-?mail/i },
  { key: 'address', label: 'Address', match: /^(address|street|addr|address ?1|full ?address)$/i },
  { key: 'city', label: 'City', match: /city|town/i },
  { key: 'state', label: 'State', match: /^(state|st|province)$/i },
  { key: 'zip', label: 'ZIP', match: /zip|postal/i },
  { key: 'website', label: 'Website', match: /web|site|url|domain/i },
  { key: 'social', label: 'Facebook / Instagram', match: /facebook|instagram|social|ig\b|fb\b/i },
  { key: 'tier', label: 'Tier (A, B, C)', match: /^(tier|fit|grade|priority)$/i },
  { key: 'notes', label: 'Notes', match: /note|comment|remark/i },
];

// Keys a store can be recognised by. Phone is the strongest; the website
// domain and a loose name+city catch stores whose number differs.
const normName = (s) => String(s || '').toLowerCase().replace(/&/g, 'and').replace(/\b(the|inc|llc|co|corp|ltd|and)\b/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
const normCity = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '');
export const domainOf = (u) => { const m = String(u || '').toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').match(/^([a-z0-9.-]+\.[a-z]{2,})/); return m ? m[1] : ''; };
export const prospectKeys = (p) => {
  const out = [];
  const ph = e164(p.phone);
  if (ph) out.push(`p:${ph}`);
  const d = domainOf(p.website);
  if (d && !/facebook|instagram|google|yelp|wix|squarespace|godaddy/.test(d)) out.push(`w:${d}`);
  const n = normName(p.name);
  if (n.length >= 4) out.push(`n:${n}|${normCity(p.city)}`);
  return out;
};

const autoMap = (headers) => {
  const m = {};
  const used = new Set();
  for (const t of TARGETS) {
    const h = headers.find((x) => !used.has(x) && t.match.test(String(x).trim()));
    if (h) { m[t.key] = h; used.add(h); }
  }
  if (!m.name && headers.length) m.name = headers[0];
  return m;
};

export default function ImportProspectsModal({ client, storeId, existing, reps, me, userEmail, onClose, onImported, notify }) {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [map, setMap] = useState({});
  const [fileName, setFileName] = useState('');
  const [pasted, setPasted] = useState('');
  const [assign, setAssign] = useState(''); // the shared pool by default: reps grab from it as they go
  const [area, setArea] = useState('');
  const [saving, setSaving] = useState(false);
  const [parseError, setParseError] = useState('');

  const takeTable = (table) => {
    const clean = table.filter((r) => r && Object.values(r).some((v) => String(v ?? '').trim()));
    if (!clean.length) { setParseError('No rows found in that file.'); return; }
    const hs = Object.keys(clean[0]);
    setHeaders(hs);
    setMap(autoMap(hs));
    setRows(clean);
    setParseError('');
  };

  const onFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setArea((a) => a || file.name.replace(/\.(csv|xlsx|xls)$/i, '').replace(/[-_]+/g, ' ').trim());
    try {
      if (/\.(xlsx|xls)$/i.test(file.name)) {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        takeTable(XLSX.utils.sheet_to_json(ws, { defval: '' }));
      } else {
        const text = await file.text();
        const res = Papa.parse(text, { header: true, skipEmptyLines: true });
        takeTable(res.data || []);
      }
    } catch (e) {
      console.error('sales: parse failed', e);
      setParseError('Could not read that file. A CSV or an Excel sheet with a header row works.');
    }
  };

  const onPaste = () => {
    const text = pasted.trim();
    if (!text) return;
    const delim = text.includes('\t') ? '\t' : ',';
    const res = Papa.parse(text, { header: true, skipEmptyLines: true, delimiter: delim });
    takeTable(res.data || []);
    setFileName('Pasted rows');
  };

  const existingKeys = useMemo(() => { const s = new Set(); for (const p of existing) for (const k of prospectKeys(p)) s.add(k); return s; }, [existing]);

  const prepared = useMemo(() => {
    const out = { ready: [], dupes: 0, noName: 0, noPhone: 0, dupeNames: [] };
    const seen = new Set();
    for (const r of rows) {
      const get = (k) => (map[k] ? String(r[map[k]] ?? '').trim() : '');
      const name = get('name');
      if (!name) { out.noName += 1; continue; }
      const phoneRaw = get('phone');
      const phone = e164(phoneRaw);
      if (!phone) out.noPhone += 1;
      const cand = { name, phone, website: get('website'), city: get('city') };
      const keys = prospectKeys(cand);
      if (keys.some((k) => existingKeys.has(k) || seen.has(k))) { out.dupes += 1; if (out.dupeNames.length < 8) out.dupeNames.push(name); continue; }
      for (const k of keys) seen.add(k);
      out.ready.push({
        name,
        phone: phone || phoneRaw || null,
        owner_name: get('owner_name') || null,
        email: get('email') || null,
        address: get('address') || null,
        city: get('city') || null,
        state: get('state') || null,
        zip: get('zip') || null,
        website: get('website') || null,
        social: get('social') || null,
        notes: get('notes') || null,
        tier: tierTag(get('tier')),
      });
    }
    return out;
  }, [rows, map, existingKeys]);

  const save = async () => {
    if (!prepared.ready.length || saving) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const active = reps.filter((r) => r.active !== false);
      const toInsert = prepared.ready.map(({ tier, ...r }, i) => ({
        ...r,
        store_id: storeId,
        source: 'import',
        created_by: userEmail || null,
        created_at: now,
        updated_at: now,
        tags: [area.trim(), tier].filter(Boolean),
        rep_id: assign === 'split' ? (active.length ? active[i % active.length].id : null) : assign || null,
        ...freshProspectFields(now),
      }));
      const inserted = await insertProspects(client, toInsert);
      onImported(inserted);
    } catch (e) {
      console.error('sales: import failed', e);
      notify(e?.code === '23505' ? 'Some phone numbers are already on the list. Nothing was added; remove them and try again.' : 'Import failed. Nothing was added.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open onClose={onClose} width="lg" title="Import a store list" subtitle="A CSV or Excel sheet with a header row, or rows pasted from a spreadsheet." footer={
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-slate-600">
          {rows.length ? <><span className="font-semibold text-slate-900">{prepared.ready.length}</span> to add{prepared.dupes ? <span title={prepared.dupeNames.join(', ')}> · {prepared.dupes} already on the list, skipped</span> : ''}{prepared.noName ? ` · ${prepared.noName} without a name` : ''}{prepared.noPhone ? ` · ${prepared.noPhone} without a phone` : ''}</> : 'Pick a file or paste rows'}
        </span>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={!prepared.ready.length || saving} className={btnPrimary}>{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Add {prepared.ready.length || ''} store{prepared.ready.length === 1 ? '' : 's'}</button>
        </div>
      </div>
    }>
      {!rows.length ? (
        <div className="space-y-4">
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-blue-400 cursor-pointer bg-slate-50">
            <input type="file" accept=".csv,.xlsx,.xls,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            <FileSpreadsheet className="w-8 h-8 text-slate-400 mx-auto" />
            <div className="text-sm font-semibold text-slate-700 mt-2">Choose a CSV or Excel file</div>
            <div className="text-xs text-slate-500 mt-1">Columns like Store, Phone, Owner, Address, City, Website. Any order. We match them by name.</div>
          </label>
          <div className="text-center text-xs font-semibold uppercase tracking-wider text-slate-400">or</div>
          <Field label="Paste rows from a spreadsheet" hint="Include the header row. Tabs or commas.">
            <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={6} className={`${inputCls} font-mono text-xs`} placeholder={'Store\tPhone\tCity\nBay Furniture\t(925) 555-0100\tConcord'} />
          </Field>
          <div className="flex justify-end"><button type="button" onClick={onPaste} disabled={!pasted.trim()} className={btnGhost}>Use pasted rows</button></div>
          {parseError && <p className="text-sm text-red-600">{parseError}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm text-slate-700"><span className="font-semibold">{fileName}</span> · {rows.length} row{rows.length === 1 ? '' : 's'}</div>
            <button type="button" onClick={() => { setRows([]); setHeaders([]); setMap({}); setFileName(''); }} className="text-xs font-semibold text-blue-700 underline">Start over</button>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Which column is which</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TARGETS.map((t) => (
                <label key={t.key} className="flex items-center gap-2 text-sm">
                  <span className={`w-36 flex-shrink-0 ${t.required ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{t.label}{t.required ? ' *' : ''}</span>
                  <select value={map[t.key] || ''} onChange={(e) => setMap({ ...map, [t.key]: e.target.value })} className="flex-1 h-9 px-2 rounded-lg border border-slate-200 bg-white text-sm min-w-0">
                    <option value="">Skip</option>
                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </div>

          <Field label="Area or list name" hint="Saved on every store in this batch, so you can find the batch later. City and state works well.">
            <input value={area} onChange={(e) => setArea(e.target.value)} className={inputCls} placeholder="Sacramento, CA" />
          </Field>

          <Field label="Give the stores to" hint="The pool is one big shared list. A store becomes a rep's the moment it comes up on their run card, so no two reps ever work the same one.">
            <select value={assign} onChange={(e) => setAssign(e.target.value)} className={inputCls}>
              <option value="">The shared pool. Reps grab from it as they go</option>
              {reps.length > 1 && <option value="split">Split evenly between {reps.map((r) => r.name).join(' and ')}</option>}
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">First rows as they will be added</div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                  <tr><th className="text-left px-3 py-2">Store</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">Owner</th><th className="text-left px-3 py-2">City</th><th className="text-left px-3 py-2">Website</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {prepared.ready.slice(0, 6).map((r, i) => (
                    <tr key={i}><td className="px-3 py-2 font-semibold text-slate-900">{r.name}</td><td className="px-3 py-2 font-mono">{r.phone || <span className="text-slate-300">none</span>}</td><td className="px-3 py-2">{r.owner_name || ''}</td><td className="px-3 py-2">{r.city || ''}</td><td className="px-3 py-2 truncate max-w-[160px]">{r.website || ''}</td></tr>
                  ))}
                  {!prepared.ready.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Nothing to add. Check the Store name column.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Sheet>
  );
}
